import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ChangePasswordModal({ forced = false, open, onOpenChange }) {
  const { changePassword, user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const isOpen = forced ? Boolean(user?.must_change_password) : open;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) return setErr("Password must be at least 8 characters.");
    if (next !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
      if (!forced) onOpenChange?.(false);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-modal" onEscapeKeyDown={(e) => forced && e.preventDefault()} onPointerDownOutside={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <KeyRound size={22} />
          </div>
          <DialogTitle className="text-center font-display text-2xl">
            {forced ? "Set a new password" : "Change password"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {forced ? "For security, please change your temporary password before continuing." : "Update your account password."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="cp-current-input" />
          </div>
          <div>
            <Label htmlFor="cp-new">New password</Label>
            <Input id="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="cp-new-input" />
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input id="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="cp-confirm-input" />
          </div>
          {err && <p className="text-sm text-red-600" data-testid="cp-error">{err}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="cp-submit">
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
